import _ from 'lodash'
import { BranchDesignator, CancelReason, CodeNext, ConceptStatusValidityByCid, Export, ImportWriteMode, UnvalidatedCodeNext, UserIdentity, UserRole } from '@fusion-advantage/backcompat'
import { ConceptStatusValidityInfo, ValiditySeverity } from './concept/ConceptStatusValidityInfo'
import { Appointment, appointmentIsToday, ServiceAppointmentStatus, Todo } from './dashboard/WorkItem'
import { WorkItem, WorkItemAction, WorkItemType } from './dashboard/WorkItem'
import { isUserElevated } from './User'
import { err, ok, Result } from 'neverthrow'
import { AppEnvironment } from './AppEnvironment'
import { RootRouteFormContent } from './dashboard/RootRouteContent'
import { DisplayExport } from './persistence/DisplayExport'

// the intent of this file is to have one set of functions that determines the
// allowable actions and next steps for Encounters and associated objects
// along their journeys; this is typically by checking CodeNext, but other
// variables can come into play as well.

// check Salesforce status to see if the encounter can be loaded
export const proposeApptExport = (appt: Appointment): Result<{memberGuid: string}, string> => {
	// the RAppointment zod model asserts the string a uuid
	// so we just need to make sure it is not null
	if(!appt.MemberGUID) {
		return err('Service Appointment has no assigned Member; please contact dispatch.')
	}
	switch(appt.Status) {
		case ServiceAppointmentStatus.CannotComplete:
		//case ServiceAppointmentStatus.Completed:
		case ServiceAppointmentStatus.Canceled:
			return err(`Service Appointment has unworkable status '${appt.Status}'; please contact dispatch.`)
	}
	// return not-null typed memberGuid
	return ok({memberGuid: appt.MemberGUID})
}

export const exportCancelReasons = (exp: Export): CancelReason[] => {
	return exp.Form.CancelReason.filter(reason => {
		if(exp.Form.Encounter.CodeNext === CodeNext.NotStarted) {
			return reason.PriorToFormStart
		} else {
			return !reason.PriorToFormStart
		}
	})
}

// these local conveniences here
// so that i can avoid risk of misstep in a logic re-write
const isAppointment = (workItem: WorkItem): workItem is Appointment => workItem.WorkItemType === WorkItemType.Appointment
const isTodo = (workItem: WorkItem): workItem is Todo => workItem.WorkItemType === WorkItemType.Todo

export const proposeWorkItemViewForm = (workItem: WorkItem, filterByDate: boolean): Result<void, string> => {
	if(filterByDate && isAppointment(workItem) && !appointmentIsToday(workItem)) {
		return err('Appointment is not today')
	}
	return ok()
}

export const proposeWorkItemStartEncounter = (workItem: WorkItem, dexp: DisplayExport, filterByDate: boolean): Result<{nextCodeNext: CodeNext}, string> => {
	if(filterByDate && isAppointment(workItem) && !appointmentIsToday(workItem)) {
		return err('Appointment is not today')
	}
	if(isTodo(workItem)) {
		if(workItem.ReadOnly) {
			return err('Work item is read only')
		}
		// Currently only when surfaced as an appointment
		// can an encounter be started
		return err('Work item is not an appointment')
	}
	switch(dexp.codeNext) {
		case CodeNext.NotStarted:
			return ok({nextCodeNext: CodeNext.InProgress})
	}
	return err(`Work item with code '${dexp.codeNext}' cannot be started`)
}

export const proposeWorkItemContinueForm = (workItem: WorkItem, dexp: DisplayExport, filterByDate: boolean, user: UserIdentity|null): Result<void, string> => {
	if(!user) {
		return err('no user identity available')
	}
	if(filterByDate && isAppointment(workItem) && !appointmentIsToday(workItem)) {
		return err('Appointment is not today')
	}
	if(isTodo(workItem)) {
		if(workItem.ReadOnly) {
			return err('Work item is read only')
		}
	}
	const codeNext = dexp.codeNext
	switch(codeNext) {
		case CodeNext.InProgress:
		case CodeNext.ClinicianAuditReview:
		case CodeNext.PostSubmitLabClinician:
		case CodeNext.CollaboratingPhysicianPending:
			return ok()
		case CodeNext.Level1Audit:
			if(dexp.auditorUsername && (dexp.auditorUsername === user.username)) {
				return ok()
			}
			return err('Work item is not assigned to current user')
	}
	return err(`Work item with code '${codeNext}' cannot be continued`)
}

export const proposeWorkItemJoinVideo = (workItem: WorkItem, dexp: DisplayExport, filterByDate: boolean): Result<void, string> => {
	if(!isAppointment(workItem)) {
		return err('Work item is not appointment')
	}
	if(filterByDate && !appointmentIsToday(workItem)) {
		return err('Appointment is not today')
	}
	if(dexp.branchDesignator !== BranchDesignator.VideoVisit) {
		return err('Appointment is not video visit')
	}
	return ok()
}

export const proposeWorkItemSendReminder = (workItem: WorkItem, dexp: DisplayExport, filterByDate: boolean, clinicianHasJoined: boolean, qtySentReminders: number): Result<void, string> => {
	// right now the reminders are for video appointments only
	const rVideo = proposeWorkItemJoinVideo(workItem, dexp, filterByDate)
	if(!rVideo.isOk()) {
		return err(rVideo.error)
	}
	if(!clinicianHasJoined) {
		return err('Clinician has not yet joined call')
	}
	if(qtySentReminders >= 1) {
		return err('Maximum number of reminders already sent')
	}
	return ok()
}

export const proposeWorkItemCancel = (workItem: WorkItem, dexp: DisplayExport, filterByDate: boolean): Result<void, string> => {
	// The way dashboard, and Todo and Appointment APIs currently work,
	// we need to prevent them from canceling a past encounter via the
	// appointment itself, because an encounter that is in some other part
	// of the workflow (i.e. AUDAUDIT_M) could still surface on a clinician's
	// calendar in a past day because it is still being pulled in from
	// Salesforce as a past valid appointment.
	if(filterByDate && isAppointment(workItem) && !appointmentIsToday(workItem)) {
		return err('Appointment is not today (cancel via clinician Todo item instead)')
	}
	if(isTodo(workItem)) {
		if(workItem.ReadOnly) {
			return err('Work item is read only')
		}
	}
	switch(dexp.codeNext) {
		case CodeNext.NotStarted:
		case CodeNext.InProgress:
			return ok()
	}
	return err(`Work item with code '${dexp.codeNext}' cannot be cancelled`)
}

export const proposeContentLoadIgnoreLocation = (content: RootRouteFormContent, env: AppEnvironment): Result<void, string> => {
	// 20260205
	// due to ongoing challenges with quickly and reliably acquiring location
	// in a variety of circumstances, we are no longer *requiring* it prior
	// opening and manipulating content
	return ok()
	/*
	switch(content.action) {
		case WorkItemAction.ViewFormAsDocgen:
		// TODO 20250915 temp fix for this loop before merging in
		// overarching error handle changes
		case WorkItemAction.ViewFormAsAuditor:
			return ok()
	}
	if(content.skipLocationCheck && env === AppEnvironment.Development) {
		return ok()
	}
	return err('location is required')
	*/
}

export const workItemWriteMode = (action: WorkItemAction, exp: Export): ImportWriteMode => {
	switch(action) {
		case WorkItemAction.StartEncounter:
		case WorkItemAction.ContinueForm:
			break
		default:
			return ImportWriteMode.ReadOnly
	}
	const codeNext = exp.Form.Encounter.CodeNext
	switch(codeNext) {
		case CodeNext.NotStarted:
			if(action === WorkItemAction.StartEncounter) {
				// [ISSUE-539] we know the action being taken is to start
				// the encounter, so if that is successful and the form is
				// actually opened, the CodeNext will be moved to InProgress,
				// we can grant Write in this case
				return ImportWriteMode.WriteAll
			}
			return ImportWriteMode.ReadOnly
		case CodeNext.InProgress:
		case CodeNext.ClinicianAuditReview:
		case CodeNext.PostSubmitLabClinician:
			return ImportWriteMode.WriteAll
		case CodeNext.Level1Audit:
			return ImportWriteMode.WriteAuditReview
		case CodeNext.CollaboratingPhysicianPending:
			return ImportWriteMode.WriteCollaboratingPhysician
		default:
			return ImportWriteMode.ReadOnly
	}
}

// usernames in the database have the domain stripped
// (see [api].[Form_Export_v003]); compare them that way
const usernamesMatch = (a: string, b: string): boolean => {
	const strip = (s: string) => s.replace(/@.*$/, '').trim().toLowerCase()
	return strip(a) === strip(b)
}

// a stricter write mode for an encounter opened directly from an
// /encounter/{guid} link rather than the dashboard (whose rules stay as
// workItemWriteMode): only the person whose hands the encounter is in may
// edit, and everyone else gets it read-only
export const linkedEncounterWriteMode = (writeMode: ImportWriteMode, exp: Export, identity: UserIdentity|null): ImportWriteMode => {
	if(writeMode === ImportWriteMode.ReadOnly || !identity) {
		return ImportWriteMode.ReadOnly
	}
	const encounter = exp.Form.Encounter
	let editorUsername: string|null|undefined
	switch(encounter.CodeNext) {
		case CodeNext.NotStarted:
		case CodeNext.InProgress:
		case CodeNext.ClinicianAuditReview:
		case CodeNext.PostSubmitLabClinician:
			editorUsername = encounter.Clinician_ID
			break
		case CodeNext.Level1Audit:
			editorUsername = encounter.Auditor_ID
			break
		default:
			// includes CollaboratingPhysicianPending, which has its own
			// pathing and is never edited from a link
			return ImportWriteMode.ReadOnly
	}
	if(!editorUsername || !usernamesMatch(editorUsername, identity.username)) {
		return ImportWriteMode.ReadOnly
	}
	return writeMode
}

export const proposeFormSaveDraft = (writeMode: ImportWriteMode, codeNext: UnvalidatedCodeNext): Result<void, string> => {
	switch(writeMode) {
		case ImportWriteMode.WriteAll:
		case ImportWriteMode.WriteAuditReview:
		case ImportWriteMode.WriteCollaboratingPhysician:
			break
		default:
			return err('Form is not opened with write access')
	}
	switch(codeNext) {
		case CodeNext.InProgress:
		case CodeNext.ClinicianAuditReview:
		case CodeNext.Level1Audit:
		case CodeNext.PostSubmitLabClinician:
		case CodeNext.CollaboratingPhysicianPending:
			return ok()
		default:
			return err(`Form with code '${codeNext}' cannot save draft`)
	}
}

export const proposeFormSubmitToCode = (prevCodeNext: UnvalidatedCodeNext, nextCodeNext: CodeNext, validities: ConceptStatusValidityByCid): Result<void, string> => {
	let canMove = false
	switch(prevCodeNext) {
		case CodeNext.InProgress:
			switch(nextCodeNext) {
				case CodeNext.Completed:
					canMove = true
					break
			}
			break
		case CodeNext.Level1Audit:
			switch(nextCodeNext) {
				case CodeNext.ClinicianAuditReview:
				case CodeNext.Audit1ToComplete:
					canMove = true
					break
			}
			break
		case CodeNext.ClinicianAuditReview:
			switch(nextCodeNext) {
				case CodeNext.Level1Audit:
					canMove = true
					break
			}
			break
		case CodeNext.PostSubmitLabClinician:
			switch(nextCodeNext) {
				case CodeNext.PostSubmitFormAudit:
					canMove = true
					break
			}
			break
		case CodeNext.CollaboratingPhysicianPending:
			switch(nextCodeNext) {
				case CodeNext.CollaboratingPhysicianSubmitted:
					canMove = true
					break
			}
	}
	if(!canMove) {
		return err(`Form of status '${prevCodeNext}' cannot be submitted to ${nextCodeNext}`)
	}
	for(let cid in validities) {
		let validity = validities[cid]
		const validityInfo = new ConceptStatusValidityInfo(validity)
		switch(validityInfo.severity) {
			case ValiditySeverity.Error:
				return err('Form cannot be submitted due to unresolved errors')
		}
	}
	return ok()
}

export const formDefaultSubmitTo = (codeNext: UnvalidatedCodeNext): CodeNext|null => {
	switch(codeNext) {
		case CodeNext.InProgress:
			return CodeNext.Completed
		case CodeNext.ClinicianAuditReview:
			return CodeNext.Level1Audit
		case CodeNext.PostSubmitLabClinician:
			return CodeNext.PostSubmitFormAudit
		case CodeNext.CollaboratingPhysicianPending:
			return CodeNext.CollaboratingPhysicianSubmitted
	}
	return null
}

export const proposeFormSubmitEvaluatorSignatureWipe = (nextCodeNext: CodeNext): Result<void, string> => {
	// if submitting back to clinician after audit
	// wipe the signature for resign
	switch(nextCodeNext) {
		case CodeNext.ClinicianAuditReview:
			return ok()
	}
	return err(`Form submitted to code '${nextCodeNext}' should not have evaluator signature wiped on submit`)
}

export enum DiagnosisReviewMode {
	Clinician,
	Auditor,
}

// which view of the diagnosis review component the user will see
export const formDiagnosisReviewMode = (codeNext: UnvalidatedCodeNext): DiagnosisReviewMode => {
	switch(codeNext) {
		case CodeNext.Level1Audit:
		case CodeNext.Audit1ToComplete:
		case CodeNext.CollaboratingPhysicianPending:
		case CodeNext.CollaboratingPhysicianSubmitted:
			return DiagnosisReviewMode.Auditor
		default:
			return DiagnosisReviewMode.Clinician
	}
}

export const formShouldQueryMaterialStatus = (codeNext?: UnvalidatedCodeNext): boolean => {
	if(!codeNext) {
		return true
	}
	switch(codeNext) {
		case CodeNext.InProgress:
			return true
	}
	return false
}

export const formCollaboratorMustSign = (codeNext: UnvalidatedCodeNext): boolean => {
	switch(codeNext) {
		case CodeNext.CollaboratingPhysicianPending:
			return true
	}
	return false
}

export const formOpenPageIdx = (codeNext: UnvalidatedCodeNext): number => {
	switch(codeNext) {
		case CodeNext.Level1Audit:
		case CodeNext.Audit1ToComplete:
		case CodeNext.ClinicianAuditReview:
			return 4
		case CodeNext.PostSubmitLabClinician:
			return 5
	}
	return 0
}

export const proposeFormClinicianScanLeaveBehindIfEmpty = (writeMode: ImportWriteMode, codeNext: UnvalidatedCodeNext, branchDesignator: BranchDesignator): Result<void, string> => {
	let r = proposeFormSaveDraft(writeMode, codeNext)
	if(!r.isOk()) {
		return r
	}
	if(codeNext !== CodeNext.InProgress) {
		return err(`Form with code '${codeNext}' should not scan leave behind`)
	}
	if(branchDesignator !== BranchDesignator.InHome) {
		return err('Only in-home branches should scan leave behind')
	}
	return ok()
}

export const proposeOpenLog = (authedUser: UserIdentity|null): Result<void, string> => {
	return (authedUser && isUserElevated(authedUser)) ? ok() : err('not authorized')
}

export const proposeRequestApiToken = (authedUser: UserIdentity|null): Result<void, string> => {
	return authedUser?.roles.includes(UserRole.APP_ADMIN) ? ok() : err('not authorized')
}

export const proposeShowDebugInfo = (authedUser: UserIdentity|null, env: AppEnvironment): Result<void, string> => {
	if(
		authedUser &&
		(
			env === AppEnvironment.Development ||
			isUserElevated(authedUser)
		)
	) {
		return ok()
	}
	// this really wouldn't hurt anything to be broadly available
	// but it isn't useful to normal users
	return err('not authorized')
}

export const proposeForceDevRenderer = (authedUser: UserIdentity|null, env: AppEnvironment): Result<void, string> => {
	if(
		authedUser &&
		(
			// do not allow this to be activated in non-dev environents;
			// there is no dev, debug or otherwise case where that could be
			// useful, and it has already messed up some post-prod qa work
			env === AppEnvironment.Development &&
			isUserElevated(authedUser)
		)
	) {
		return ok()
	}
	return err('not authorized')
}

export const proposeSimulateOffline = (authedUser: UserIdentity|null, env: AppEnvironment): Result<void, string> => {
	if(
		proposeShowDebugInfo(authedUser, env).isOk() ||
		authedUser?.roles.includes(UserRole.WIFI_DISABLE)
	) {
		return ok()
	}
	return err('not authorized')
}

export const proposeSetSyncStrategy = (authedUser: UserIdentity|null, env: AppEnvironment): Result<void, string> => {
	return proposeShowDebugInfo(authedUser, env)
}

export const proposeOpenStorybook = (authedUser: UserIdentity|null, env: AppEnvironment): Result<void, string> => {
	return proposeForceDevRenderer(authedUser, env)
}

export const proposeSplode = (env: AppEnvironment, isTrainingMode: boolean): Result<void, string> => {
	if(env !== AppEnvironment.Development) {
		return err('not permitted in this environment')
	}
	if(isTrainingMode) {
		return err('not available in training mode')
	}
	return ok()
}
